import { useMemo } from 'react';
import { useResourceListAll } from '@/shared/api/jsonapi/hooks';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { useEffectiveConfig } from '@/features/tenant-attributes/effective';

/** Riga di `tenant-attributes` (store chiave/valore per-tenant, BE v1.20.38). */
export interface TenantAttributeRow extends Denormalized {
  type: 'tenant-attributes';
  key?: string;
  value?: string;
  valueType?: string;
}

/**
 * Legge la configurazione/feature-flag per-tenant dallo store `tenant-attributes`
 * (le chiavi tipo `leave.*`, `smartWorking.*`, `attendance.*`, `reporting.*`,
 * `fleet.enabled`, `travel.enabled`, `meetingRoom.enabled`). Espone getter tipizzati
 * con default hardcoded (il BE applica gli stessi default se la chiave è assente).
 */
export function useTenantConfig(options?: { enabled?: boolean }) {
  /*
   * `enabled` arriva da fuori, e la ragione per cui **non** si legge qui l'autenticazione è un
   * difetto che ci siamo fatti e disfatti: chiamando `useAuth()` in questo hook, ogni test che
   * monta una pagina senza `AuthProvider` esplode con «useAuth must be used within AuthProvider» —
   * ventuno test in otto file, e nessuno di essi parla di permessi. Un hook di configurazione non
   * deve pretendere un provider di sessione.
   *
   * Chi ha bisogno della guardia è chi gira **fuori dalla shell**: da quando la palette ⌘K filtra
   * le pagine coi permessi, `useContestoAccesso` monta questo hook anche sulla pagina di login, e
   * lì una lettura di `tenant-attributes` senza token è un 401 in console su una schermata dove non
   * c'è niente da sbagliare. Quel chiamante lo stato della sessione lo ha già in mano.
   */
  const q = useResourceListAll<TenantAttributeRow>('tenant-attributes', undefined, { staleTime: 5 * 60_000, enabled: options?.enabled ?? true }, 200);

  /*
   * ⚠️ **Dal BE v1.248.0 la riga dell'azienda non basta** (task 1900): una chiave governata da
   * un'area vale quella della capogruppo se il lucchetto è chiuso, e quella dell'antenato più vicino
   * se l'azienda non ha la riga. Il server lo applica già; leggere solo le righe proprie voleva dire
   * decidere a schermo con un valore diverso. Le righe restano per le chiavi fuori area (gli
   * interruttori dei moduli, `portal.`…), che non salgono; se la lettura effettiva non risponde si
   * resta alle righe, come prima.
   */
  const eff = useEffectiveConfig(undefined, { enabled: options?.enabled ?? true });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of q.data?.data ?? []) if (a.key) m.set(a.key, a.value ?? '');
    for (const a of eff.data?.attributes ?? []) {
      /* `default` senza riga in catena: il server applica il default del catalogo. Lo si mette
         solo se manca una riga, per non cambiare il default che ogni getter già porta con sé. */
      if (a.level === 'default') continue;
      if (a.value != null) m.set(a.key, a.value);
    }
    return m;
  }, [q.data, eff.data]);

  /*
   * I getter e l'oggetto di ritorno sono memoizzati sulla mappa, non ricreati a ogni render.
   * Non è pulizia: `getBool` è finito fra le dipendenze di chi calcola la visibilità del menu
   * (`app/layouts/usePermContext.ts`), e un'identità nuova a ogni render vi propaga un ricalcolo
   * che quel `useMemo` esisteva per evitare.
   */
  return useMemo(() => {
    const getString = (key: string, def = ''): string => (map.has(key) ? (map.get(key) ?? def) : def);
    const getInt = (key: string, def = 0): number => {
      const v = map.get(key);
      const n = v == null ? NaN : Number(v);
      return Number.isFinite(n) ? n : def;
    };
    const getBool = (key: string, def = false): boolean => {
      const v = map.get(key);
      if (v == null) return def;
      return v === 'true' || v === '1';
    };
    return { getString, getInt, getBool, has: (k: string) => map.has(k), isLoading: q.isLoading, raw: map };
  }, [map, q.isLoading]);
}
